import { Attachments } from '../attachments/Attachments'
// The record, as today's issue page shows its Comments and its Activity
// (Poulpe-513): two sections, never collapsed, read-only, as the server only
// ever appends to the record.
//
// Comments are its discussion, each with its replies, oldest first. From four
// threads the list is an index, as today's (SUN-1268; Fabien, 5 Oct 2026:
// "Index, latest open"): a thread is open, as every thread of a shorter list
// is, or folded to one row with its time, its author and its first words. A
// thread starts open when it is the last one, the one that holds the newest
// comment or reply, or the one a link names. From then on only the reader
// opens or folds it. A thread never opened is its row alone; once opened its
// content stays on the page, hidden while folded, so a reply in progress
// outlives a fold.
//
// The Record is every other entry, newest first, its 15 newest shown until
// the reader asks for all, as today's Activity.
//
// A link that names an entry (`#entry-<number>` or today's `#comment-<id>`)
// opens what holds it and scrolls to it; so does a comment just added.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { RecordEntry } from '../api/types'
import { ChevronRightIcon } from '../components/Icons'
import { clockTime, exactTime, Time } from '../components/Time'
import { SectionHeader } from '../components/layout'
import { Markdown } from '../markdown/Markdown'
import { plainSnippet } from '../search/snippet'
import { actorName, RECORD_KIND_NAMES } from '../vocab'
import { useStore } from '../store/context'
import { IssueComment } from './IssueComment'

export interface Thread {
  entry: RecordEntry
  replies: RecordEntry[]
}

/** The comment an entry replies to: a discussion (or an imported comment) that points to another entry. */
function parentOf(entry: RecordEntry): string | undefined {
  if (entry.kind !== 'discussion' && entry.kind !== 'note') return undefined
  return entry.refs?.find((ref) => ref.kind === 'entry')?.id
}

/** The entries in order, each reply under its comment, one level deep; a reply whose comment is missing stands alone. */
export function threadsOf(entries: readonly RecordEntry[]): Thread[] {
  const ids = new Map(entries.map((entry) => [entry.id, entry]))
  const tops = new Map<string, RecordEntry[]>()
  const top = (entry: RecordEntry) => {
    const parent = parentOf(entry)
    const found = parent ? ids.get(parent) : undefined
    return found && parentOf(found) === undefined ? found : undefined
  }
  for (const entry of entries) {
    const parent = top(entry)
    if (parent) tops.set(parent.id, [...(tops.get(parent.id) ?? []), entry])
  }
  return entries.filter((entry) => !top(entry)).map((entry) => ({ entry, replies: tops.get(entry.id) ?? [] }))
}

/** Whether a thread is a comment's, and so shows under Comments rather than the Record. */
const isComment = ({ entry }: Thread) => entry.kind === 'discussion'

/** Today's issue fragments name a source comment, including a reply, rather than a record number. */
function importedComment(hash: string, entries: readonly RecordEntry[]): RecordEntry | undefined {
  if (!hash.startsWith('#comment-')) return undefined
  let id: string
  try { id = decodeURIComponent(hash.slice('#comment-'.length)) } catch { return undefined }
  return entries.find((entry) => entry.import_source?.toLowerCase() === `comment:${id.toLowerCase()}`)
}

/** The element id the link names, `entry-<number>`, or null. */
function useAnchor(entries: readonly RecordEntry[]): { anchor: string | null; arrival: string } {
  const { hash, key } = useLocation()
  const imported = importedComment(hash, entries)
  const anchor = hash.startsWith('#entry-') ? hash.slice(1) : imported ? `entry-${imported.number}` : null
  return { anchor, arrival: `${key} ${hash}` }
}

const holds = (thread: Thread, test: (entry: RecordEntry) => boolean) => test(thread.entry) || thread.replies.some(test)

/** When an entry was written: its source's time for an imported one. */
const writtenAt = (entry: RecordEntry) => Date.parse(entry.historical?.at ?? entry.at)

/** When a thread was last written to: its comment, or the newest of its replies. */
function lastWritten({ entry, replies }: Thread): number {
  return replies.reduce((at, reply) => Math.max(at, writtenAt(reply)), writtenAt(entry))
}

/** From this many threads the list is an index: each thread open, or folded to one row. */
export const INDEX_FROM = 4

/**
 * The threads an index shows open without a click: the last one, and the one
 * that holds the newest comment or reply when that is another.
 */
export function openByDefault(threads: readonly Thread[]): string[] {
  const last = threads.at(-1)
  if (!last) return []
  let newest = last
  for (const thread of threads) {
    if (lastWritten(thread) > lastWritten(newest)) newest = thread
  }
  return newest === last ? [last.entry.id] : [last.entry.id, newest.entry.id]
}

/** How much of a comment its row carries; the stylesheet cuts what the row has no room for. */
const FIRST_WORDS_MAX = 200

/** A comment's first words as one line of text, markdown's marks gone, `…` where it was cut. */
export function firstWords(body: string): string {
  // Only the head of a long comment is read: enough to fill the row whatever its markup.
  const text = plainSnippet(body.slice(0, 4 * FIRST_WORDS_MAX))
  if (text.length <= FIRST_WORDS_MAX) return text
  const space = text.lastIndexOf(' ', FIRST_WORDS_MAX)
  return `${text.slice(0, space > 0 ? space : FIRST_WORDS_MAX)}…`
}

/**
 * How a thread of an index shows: in full; as its row, with its content
 * still on the page, hidden; or as its row and nothing else, never opened.
 */
type Fold = 'open' | 'folded' | 'row'

/** The name of an open thread's button, which shows a chevron and no word. */
const FOLD = 'Fold this comment'

/** The record's discussion, as today's Comments; `reveal` is a comment just added, opened and scrolled to. */
export function IssueComments({ entries, reveal, onReplied }: { entries: readonly RecordEntry[]; reveal?: string | null; onReplied?: (id: string) => void }) {
  const listId = useId()
  const section = useRef<HTMLElement>(null)
  const [replying, setReplying] = useState<string | null>(null)
  const threads = useMemo(() => threadsOf(entries).filter(isComment), [entries])
  const count = threads.reduce((sum, thread) => sum + 1 + thread.replies.length, 0)
  const isIndex = threads.length >= INDEX_FROM

  // What leads here: a link that names an entry, or a comment just added. Each is followed once, when it is on the page.
  const { anchor, arrival } = useAnchor(entries)
  const linkedId = threads.find((thread) => holds(thread, (entry) => `entry-${entry.number}` === anchor))?.entry.id ?? null
  const revealedId = reveal ? threads.find((thread) => holds(thread, (entry) => entry.id === reveal))?.entry.id ?? null : null
  const [followed, setFollowed] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<string | null>(null)
  const arriving = linkedId !== null && followed !== arrival
  const revealing = revealedId !== null && revealed !== reveal

  // Open without a click: every thread of a short list; of an index, its defaults and the thread a link names.
  const byDefault = useMemo(() => {
    const ids = new Set(isIndex ? openByDefault(threads) : threads.map((thread) => thread.entry.id))
    if (linkedId !== null) ids.add(linkedId)
    return ids
  }, [threads, isIndex, linkedId])
  // The defaults say how a thread starts, when it comes on the list; from then on only the reader changes it.
  const [folds, setFolds] = useState<ReadonlyMap<string, Fold>>(
    () => new Map(threads.map(({ entry }) => [entry.id, byDefault.has(entry.id) ? 'open' : 'row'])),
  )
  const isOpen = (id: string) => {
    // A short list shows every thread in full, and what leads here opens its thread, whoever folded it.
    if (!isIndex || (arriving && id === linkedId) || (revealing && id === revealedId)) return true
    const fold = folds.get(id)
    return fold === undefined ? byDefault.has(id) : fold === 'open'
  }

  // A thread that has just come on the list is recorded as it starts; one shown open, as open: it may be being read.
  // Before the page is painted, so that no click of the reader's comes between showing and recording.
  const unrecorded = threads.flatMap(({ entry: { id } }): [string, Fold][] => {
    const open = isOpen(id)
    return (open ? folds.get(id) === 'open' : folds.has(id)) ? [] : [[id, open ? 'open' : 'row']]
  })
  useLayoutEffect(() => {
    if (unrecorded.length > 0) setFolds((before) => new Map([...before, ...unrecorded]))
  })

  // Scroll once what leads here is on the page, its thread open by then.
  useEffect(() => {
    if (!arriving || !section.current) return
    const target = [...section.current.querySelectorAll<HTMLElement>('[id]')].find((element) => element.id === anchor)
    target?.scrollIntoView?.({ block: 'center' })
    setFollowed(arrival)
  }, [arriving, anchor, arrival])
  useEffect(() => {
    if (!revealing || !section.current) return
    const target = [...section.current.querySelectorAll<HTMLElement>('[data-record-id]')].find((element) => element.dataset.recordId === reveal)
    target?.scrollIntoView?.({ block: 'center' })
    setRevealed(reveal ?? null)
  }, [revealing, reveal])

  const setOpen = (id: string, open: boolean) => setFolds((before) => new Map(before).set(id, open ? 'open' : 'folded'))
  const allOpen = threads.every((thread) => isOpen(thread.entry.id))
  const openAll = () => setFolds(new Map<string, Fold>(threads.map(({ entry }) => [entry.id, 'open'])))
  // Back to what an index opens without a click. Every thread is open by then: the others stay on the page, hidden.
  const foldEarlier = () => setFolds(new Map<string, Fold>(threads.map(({ entry }) => [entry.id, byDefault.has(entry.id) ? 'open' : 'folded'])))

  return (
    <section ref={section} className="section comments">
      <SectionHeader
        title="Comments"
        count={count}
        actions={isIndex && (
          <button type="button" className="button button-quiet button-small" onClick={allOpen ? foldEarlier : openAll}>
            {allOpen ? 'Fold earlier' : 'Open all'}
          </button>
        )}
      />
      {threads.length === 0 ? (
        <p className="muted">No comments yet.</p>
      ) : (
        <ol className="record-list">
          {threads.map((thread) => {
            const { id } = thread.entry
            const open = isOpen(id)
            return (
              <RecordItem
                key={id}
                entry={thread.entry}
                replies={thread.replies}
                fold={isIndex ? { open, onPage: open || folds.get(id) === 'folded', bodyId: `${listId}-${id}`, toggle: () => setOpen(id, !open) } : undefined}
                replying={replying === id}
                onReply={onReplied ? (opened) => setReplying(opened ? id : null) : undefined}
                onReplied={onReplied}
              />
            )
          })}
        </ol>
      )}
    </section>
  )
}

/** How many of the Record's newest entries show until the reader asks for all, as today's Activity. */
export const RECORD_PREVIEW = 15

/** Every entry of the record that is not a comment, newest first, as today's Activity. */
export function IssueRecord({ entries }: { entries: readonly RecordEntry[] }) {
  const section = useRef<HTMLElement>(null)
  const [all, setAll] = useState(false)
  const threads = useMemo(() => threadsOf(entries).filter((thread) => !isComment(thread)).reverse(), [entries])
  const { anchor, arrival } = useAnchor(entries)
  const linked = threads.findIndex((thread) => holds(thread, (entry) => `entry-${entry.number}` === anchor))
  const [followed, setFollowed] = useState<string | null>(null)
  const arriving = linked >= 0 && followed !== arrival
  // A link to an entry beyond the newest shows them all.
  const shown = all || (arriving && linked >= RECORD_PREVIEW) ? threads : threads.slice(0, RECORD_PREVIEW)
  useEffect(() => {
    if (!arriving || !section.current) return
    const target = [...section.current.querySelectorAll<HTMLElement>('[id]')].find((element) => element.id === anchor)
    target?.scrollIntoView?.({ block: 'center' })
    if (linked >= RECORD_PREVIEW) setAll(true)
    setFollowed(arrival)
  }, [arriving, anchor, arrival, linked])
  if (threads.length === 0) return null
  return (
    <section ref={section} className="section record">
      <SectionHeader
        title="Record"
        count={threads.reduce((sum, thread) => sum + 1 + thread.replies.length, 0)}
        actions={threads.length > RECORD_PREVIEW && (
          <button type="button" className="button button-quiet button-small" onClick={() => setAll(!all)}>
            {all ? 'Show recent' : `Show all ${threads.length}`}
          </button>
        )}
      />
      <ol className="record-list">
        {shown.map(({ entry, replies }) => (
          <RecordItem key={entry.id} entry={entry} replies={replies} />
        ))}
      </ol>
    </section>
  )
}

/** A thread's place in an index: whether it is open, whether its content is on the page, and its button. */
interface ThreadFold {
  open: boolean
  onPage: boolean
  bodyId: string
  toggle: () => void
}

function RecordItem({ entry, replies = [], fold, replying = false, onReply, onReplied }: {
  entry: RecordEntry
  replies?: readonly RecordEntry[]
  fold?: ThreadFold
  replying?: boolean
  onReply?: (open: boolean) => void
  onReplied?: (id: string) => void
}) {
  const store = useStore()
  const { author } = entry
  const classes = ['record-entry', fold && 'is-index', fold && !fold.open && 'is-folded'].filter(Boolean).join(' ')
  const content = (
    <>
      <div className="record-head">
        <span className="tag">{RECORD_KIND_NAMES[entry.kind] ?? entry.kind}</span>
        {store.isRecordPending(entry.id) && <span className="tag">Pending</span>}
        <span className="record-author" title={author.kind === 'agent' ? `Session ${author.session}` : undefined}>
          {entry.historical?.author ?? actorName(author)}
        </span>
        <Time iso={entry.historical?.at ?? entry.at} className="record-time" />
      </div>
      <Markdown source={entry.body} />
      <Attachments target={{ issue: entry.issue, entry: entry.id }} author={entry.author} readOnly={entry.kind !== 'discussion' || !!entry.historical || store.isRecordPending(entry.id)} />
      {entry.kind === 'discussion' && onReply && !replying && (
        <button type="button" className="button record-reply" onClick={() => onReply(true)}>Reply</button>
      )}
      {(replies.length > 0 || replying) && (
        <ol className="record-replies">
          {replies.map((reply) => (
            <RecordItem key={reply.id} entry={reply} />
          ))}
          {replying && (
            <li className="record-entry">
              <IssueComment issueId={entry.issue} parent={entry.id} onAdded={(id) => onReplied?.(id)} onClose={() => onReply?.(false)} />
            </li>
          )}
        </ol>
      )}
    </>
  )
  return (
    <li className={classes} id={`entry-${entry.number}`} data-record-id={entry.id}>
      {fold ? (
        <>
          {/* One button in both states, so it keeps the focus: the row of a folded thread, the fold of an open one. */}
          <button
            type="button"
            className="comment-toggle"
            aria-expanded={fold.open}
            aria-controls={fold.onPage ? fold.bodyId : undefined}
            aria-label={fold.open ? FOLD : undefined}
            title={fold.open ? FOLD : undefined}
            onClick={fold.toggle}
          >
            <ChevronRightIcon size={12} />
            {!fold.open && <ThreadLine entry={entry} replies={replies.length} />}
          </button>
          {fold.onPage && <div className="comment-thread-body" id={fold.bodyId} hidden={!fold.open}>{content}</div>}
        </>
      ) : content}
    </li>
  )
}

/** What a folded thread's row says, on one line: when and by whom, the comment's first words, and how many replies it holds. */
function ThreadLine({ entry, replies }: { entry: RecordEntry; replies: number }) {
  const at = entry.historical?.at ?? entry.at
  // The spaces are for what reads the row aloud: laid out, its parts are apart already.
  return (
    <>
      {/* A timestamp, not an age (Fabien, 5 Oct 2026): an index is read for which comment is which. */}
      <time dateTime={at} title={exactTime(at)}>{clockTime(at, Date.now())}</time>{' '}
      <span className="comment-toggle-author">{entry.historical?.author ?? actorName(entry.author)}</span>{' '}
      <span className="comment-toggle-words">{firstWords(entry.body)}</span>
      {replies > 0 && (
        <>
          {' '}
          <span className="comment-toggle-replies">{replies === 1 ? '1 reply' : `${replies} replies`}</span>
        </>
      )}
    </>
  )
}
